import { ReactComponent as AddIcon } from '@epam/assets/icons/common/action-add-18.svg';
import {
  Button,
  FlexCell,
  FlexRow,
  Panel,
  Tag,
  Text,
  TextInput,
} from '@epam/promo';
import { useRef } from 'react';

import { UserRolePicker } from 'components/permissions/PermissionPicker';
import { useModal } from 'hooks';
import { useAdminCreateQuery } from 'hooks/api/useAdminCreateQuery';
import { USER_ROLE } from 'models/user.model';
import {
  pushEmailToList,
  removeEmailFromList,
  revertEmailAsInput,
  updateInputValue,
  updateRole,
  usePermissionStore,
} from 'store/permission';
import css from './PermissionForm.module.scss';

const MAX_ALLOWED_EMAILS = 5;

export const PermissionForm = () => {
  const modal = useModal();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailList = usePermissionStore((state) => state.emails);
  const emailInput = usePermissionStore((state) => state.input);
  const adminRole = usePermissionStore((state) => state.role);
  const errorMessage = usePermissionStore((state) => state.errorMessage);
  const { isLoading: isMutationProcessing, mutate: addAdmin } =
    useAdminCreateQuery();

  const handleInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      pushEmailToList(emailInput);
    }

    if (event.key === 'Backspace' && emailInput === '') {
      revertEmailAsInput(emailList[emailList.length - 1]);
    }
  };

  return (
    <>
      <FlexRow cx={css.form}>
        <FlexCell grow={4}>
          <FlexRow
            cx={{
              [css['form__input-group']]: true,
              [css['form__input-group_invalid']]: errorMessage,
              [css['form__input-group_disabled']]: isMutationProcessing,
            }}>
            <FlexCell
              cx={{
                [css.form__input_email]: true,
                [css['form__input_email--error']]: errorMessage,
              }}
              grow={5}>
              <Panel cx={`${css['form__emails-list']}`}>
                {emailList.map((email: string) => (
                  <Tag
                    key={email}
                    caption={email}
                    cx={css['form__email-tag']}
                    size="24"
                    onClear={() => removeEmailFromList(email)}
                  />
                ))}
              </Panel>
              {emailList.length < MAX_ALLOWED_EMAILS && (
                <TextInput
                  ref={emailInputRef}
                  inputMode="email"
                  placeholder="Email, press space or enter to separate"
                  size="36"
                  value={emailInput}
                  onKeyDown={handleInputKeyPress}
                  onValueChange={updateInputValue}
                />
              )}
            </FlexCell>
            <FlexCell cx={css.form__input_role} grow={2}>
              <UserRolePicker value={adminRole} onValueChange={updateRole} />
            </FlexCell>
          </FlexRow>
        </FlexCell>
        <FlexCell grow={1.5} style={{ alignSelf: 'baseline' }}>
          <Button
            caption={isMutationProcessing ? 'Processing...' : 'Add new user(s)'}
            color="blue"
            cx={css.form__btn}
            icon={AddIcon}
            isDisabled={!emailList.length || isMutationProcessing}
            size="36"
            onClick={() =>
              modal.confirm({
                text: `Are you sure you want to add user(s):
              ${emailList.join('\n')} \n with ${USER_ROLE[adminRole]} role?`,
                highlights: [USER_ROLE[adminRole], 'add', ...emailList],
                onSuccess: () => {
                  addAdmin({ role: adminRole, emails: emailList });
                  emailInputRef.current?.focus();
                },
                onAbort: () => {},
              })
            }
          />
        </FlexCell>
      </FlexRow>
      {errorMessage && (
        <Text cx={css['form__invalid-message']}>{errorMessage}</Text>
      )}
    </>
  );
};
